import PasswordInput from '../components/PasswordInput';
import { formatPhone, formatCnic } from '../utils/employeeFormatters';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

import {
  PlusIcon, PencilIcon, XMarkIcon, ClockIcon,
  BriefcaseIcon, ArrowRightOnRectangleIcon, NoSymbolIcon,
  CheckCircleIcon, MagnifyingGlassIcon, LockClosedIcon, LockOpenIcon,
} from '@heroicons/react/24/outline';

const parseDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const dt = new Date(d);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth()+1).padStart(2,'0');
  const day = String(dt.getDate()).padStart(2,'0');
  return y + '-' + mo + '-' + day;
};


const ini  = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'C';
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ca*{box-sizing:border-box;}
  .ca{font-family:'Sora',sans-serif;color:#1a1d2e;animation:caFade .4s ease both;}
  @keyframes caFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .ca-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .ca-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .ca-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 18px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-purple{background:#8b5cf6;color:white;} .btn-purple:hover{box-shadow:0 4px 14px rgba(139,92,246,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-amber{background:#fffbeb;color:#d97706;border:1px solid #fde68a;} .btn-amber:hover{background:#d97706;color:white;}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-sm{padding:5px 11px;font-size:.74rem;}
  .btn:disabled{opacity:.5;cursor:not-allowed;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-hdr{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;}
  .tbl{width:100%;border-collapse:collapse;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl th:last-child{text-align:right;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{padding:11px 16px;font-size:.8rem;color:#3a3f58;vertical-align:middle;} .tbl td:last-child{text-align:right;}
  .nc{display:flex;align-items:center;gap:10px;}
  .av{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#8b5cf6,#3b6ef8);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ne{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.74rem;color:#6b7194;}
  .pill{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 9px;border-radius:99px;}
  .pill.active{background:#ecfdf5;color:#10c98e;} .pill.resigned{background:#fef2f2;color:#ef4444;}
  .pill.terminated{background:#1a1d2e;color:white;} .pill.inactive{background:#f0f2f7;color:#9094a8;}
  .grade-tag{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 8px;border-radius:99px;background:#f3f0ff;color:#8b5cf6;margin:1px;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:3px;transition:.13s;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.edit{color:#3b6ef8;} .act-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;}
  .act-btn.resign{color:#d97706;} .act-btn.resign:hover{background:#fffbeb;border-color:#fde68a;}
  .act-btn.terminate{color:#ef4444;} .act-btn.terminate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.history{color:#8b5cf6;} .act-btn.history:hover{background:#f3f0ff;border-color:#8b5cf6;}
  .act-btn.reactivate{color:#10c98e;} .act-btn.reactivate:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.deactivate{color:#ef4444;} .act-btn.deactivate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.activate{color:#10c98e;} .act-btn.activate:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.del{color:#ef4444;} .act-btn.del:hover{background:#fef2f2;border-color:#fecaca;}
  .empty{text-align:center;padding:48px 20px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#8b5cf6;border-radius:50%;animation:sp .7s linear infinite;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;}
  .m-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-icon.purple{background:#f3f0ff;color:#8b5cf6;} .m-icon.amber{background:#fffbeb;color:#d97706;}
  .m-icon.red{background:#fef2f2;color:#ef4444;} .m-icon.green{background:#ecfdf5;color:#10c98e;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;} .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fta,.fsel{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fta:focus,.fsel:focus{border-color:#8b5cf6;background:white;box-shadow:0 0 0 3px rgba(139,92,246,.08);}
  .fi.mono{font-family:'JetBrains Mono',monospace;} .fta{resize:vertical;min-height:72px;}
  .fh{font-size:.68rem;color:#b0b5cc;}
  .fdiv{grid-column:1/-1;display:flex;align-items:center;gap:10px;margin:4px 0;}
  .fdiv-lbl{font-size:.65rem;font-weight:700;color:#c0c5dc;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
  .fdiv-line{flex:1;height:1px;background:#f0f2f8;}
  .warn-box{border-radius:10px;padding:10px 14px;font-size:.76rem;line-height:1.55;margin-bottom:14px;}
  .warn-amber{background:#fffbeb;border:1px solid #fde68a;color:#92400e;}
  .warn-red{background:#fef2f2;border:1px solid #fecaca;color:#991b1b;}
  .search-wrap{position:relative;flex:1;max-width:280px;}
  .search-wrap svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);width:14px;height:14px;color:#9094a8;stroke-width:2;}
  .search-inp{width:100%;font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:9px;outline:none;background:#f8f9fc;color:#1a1d2e;}
  .search-inp:focus{border-color:#8b5cf6;background:white;}
  .toggle-row{display:flex;align-items:center;gap:8px;font-size:.78rem;color:#6b7194;cursor:pointer;user-select:none;}
  .grade-list{display:flex;flex-direction:column;gap:6px;max-height:180px;overflow-y:auto;}
  .grade-item{display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:9px;border:1.5px solid #e0e4f0;background:#f8f9fc;cursor:pointer;font-size:.8rem;font-weight:500;color:#3a3f58;transition:.15s;}
  .grade-item.on{border-color:#8b5cf6;background:#f3f0ff;color:#6d28d9;}
  .grade-chk{width:17px;height:17px;border-radius:5px;border:2px solid #d1d5db;background:white;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:.15s;}
  .grade-item.on .grade-chk{background:#8b5cf6;border-color:#8b5cf6;}
  .grade-chk svg{width:10px;height:10px;stroke:white;stroke-width:3;opacity:0;}
  .grade-item.on .grade-chk svg{opacity:1;}
  .cards{display:none;padding:10px;}
  .card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .card-row{display:flex;justify-content:space-between;align-items:flex-start;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .card-row:last-of-type{border-bottom:none;}
  .card-label{color:#9094a8;font-weight:600;white-space:nowrap;flex-shrink:0;}
  .card-actions{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;justify-content:flex-end;}
  @media(max-width:768px){.ca-title{font-size:1.2rem;}.tbl-wrap{display:none!important;}.cards{display:block;}.m-body{padding:16px;}.fg{grid-template-columns:1fr;}.ff.full{grid-column:1;}.fdiv{grid-column:1;}}
`;

const FD = ({label}) => <div className="fdiv"><span className="fdiv-lbl">{label}</span><div className="fdiv-line"/></div>;
const emptyForm = { name:'', email:'', password:'', employeeId:'', mobile:'', cnic:'', address:'', joiningDate:'', exitDate:'', assignedGrades:[] };

export default function CoordinatorsAdmin() {
  const navigate = useNavigate();
  const hasLoaded = useRef(false);
  const [coordinators, setCoordinators] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [classes,      setClasses]      = useState([]);
  const [modal,        setModal]        = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const [exitModal,    setExitModal]    = useState(null);
  const [exitForm,     setExitForm]     = useState({ exitDate:'', exitNote:'', joiningDate:'' });
  const [saving,       setSaving]       = useState(false);
  const [search,       setSearch]       = useState('');
  const [statusModal,  setStatusModal]  = useState(null); // { coord, action: 'deactivate'|'activate'|'reactivate' }
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => { loadCoordinators(); loadClasses(); }, [showInactive]);

  const loadCoordinators = async () => {
    if (!hasLoaded.current) setLoading(true);
    try {
      const { data } = await api.get(`/admin/coordinators?includeInactive=${showInactive}`);
      hasLoaded.current = true;
      hasLoaded.current = true;
      setCoordinators(data||[]);
    } catch { toast.error('Failed to load coordinators'); }
    finally { setLoading(false); }
  };

  const loadClasses = async () => {
    try { const { data } = await api.get('/admin/classes'); setClasses(Array.isArray(data)?data:[]); } catch {}
  };

  const openAdd = async () => {
    setEditing(null);
    setForm({ ...emptyForm, email:'', password:'', name:'', joiningDate: new Date().toISOString().split('T')[0] });
    setModal(true);
    try {
      const { data } = await api.get('/admin/coordinators/next-employee-id');
      setForm(prev => ({ ...prev, employeeId: data.employeeId || prev.employeeId }));
    } catch {}
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name:           c.user?.name    || '',
      email:          c.user?.email   || '',
      password:       '',
      employeeId:     c.employeeId    || '',
      mobile:         c.mobile        || '',
      cnic:           c.cnic          || '',
      address:        c.address       || '',
      joiningDate:    c.joiningDate   ? parseDate(c.joiningDate) : '',
      exitDate:       c.exitDate      ? parseDate(c.exitDate)   : '',
      assignedGrades: (c.assignedGrades||[]).map(g => String(g.classId?._id||g.classId||g)),
    });
    setModal(true);
  };

  const openExit = (c, exitType) => {
    setExitModal({ coord:c, exitType });
    setExitForm({
      exitDate:    new Date().toISOString().split('T')[0],
      exitNote:    '',
      joiningDate: c.joiningDate ? parseDate(c.joiningDate) : '',
    });
  };

  const toggleGrade = (classId) => {
    const id = String(classId);
    setForm(p => ({
      ...p,
      assignedGrades: p.assignedGrades.includes(id)
        ? p.assignedGrades.filter(g=>g!==id)
        : [...p.assignedGrades, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())  { toast.error('Name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }

    // Check for duplicate class assignments — warn if another coordinator already has this class
    if (form.assignedGrades.length > 0) {
      const conflicts = [];
      for (const gradeId of form.assignedGrades) {
        const existing = coordinators.find(co => {
          if (editing && String(co._id) === String(editing._id)) return false; // skip self
          const grades = co.assignedGrades || [];
          return grades.some(g => {
            const gId = String(g.classId?._id || g.classId || g);
            return gId === String(gradeId);
          });
        });
        if (existing) {
          const cls = classes.find(cl => String(cl._id) === String(gradeId));
          conflicts.push({ className: cls?.name || gradeId, coordName: existing.user?.name || existing.name });
        }
      }
      if (conflicts.length > 0) {
        const msg = conflicts.map(x => x.className + ' is already assigned to ' + x.coordName).join(', ');
        const confirmed = window.confirm('Conflict detected:\n\n' + msg + '\n\nDo you want to replace and reassign?');
        if (!confirmed) return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        assignedGrades: form.assignedGrades.map(id => ({ classId:id, sections:[] })),
        exitDate: form.exitDate || undefined,
      };
      if (editing) {
        await api.put(`/admin/coordinators/${editing._id}`, payload);
        toast.success('Coordinator updated');
      } else {
        await api.post('/admin/coordinators', payload);
        toast.success('Coordinator created');
      }
      setModal(false); loadCoordinators();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const submitExit = async () => {
    if (!exitForm.exitNote.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      const userId = exitModal.coord.user?._id || exitModal.coord._id;
      await api.put(`/admin/coordinators/${userId}/exit`, {
        exitType:    exitModal.exitType,
        exitDate:    exitForm.exitDate,
        exitNote:    exitForm.exitNote,
        joiningDate: exitForm.joiningDate,
      });
      toast.success(`Coordinator marked as ${exitModal.exitType}`);
      setExitModal(null); loadCoordinators();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const openStatusModal = (c, action) => setStatusModal({ coord: c, action });

  const confirmStatusAction = async () => {
    if (!statusModal) return;
    const { coord, action } = statusModal;
    const userId = coord.user?._id || coord._id;
    setStatusSaving(true);
    try {
      if (action === 'reactivate') {
        await api.put(`/admin/coordinators/${userId}/reactivate`);
        toast.success('Reactivated');
      } else {
        await api.put(`/admin/user-status/${userId}`, { active: action === 'activate' });
        toast.success(action === 'activate' ? 'Coordinator activated' : 'Coordinator deactivated');
      }
      setStatusModal(null); loadCoordinators();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setStatusSaving(false); }
  };

  const getStatus = (c) => {
    if (!c.isActive && c.exitType) return c.exitType;
    if (!c.isActive) return 'inactive';
    return 'active';
  };

  // #11 — filter before render
  const coordFiltered = coordinators.filter(c =>
    !search ||
    (c.user?.name||c.name||'').toLowerCase().includes(search.toLowerCase()) ||
    (c.employeeId||'').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{css}</style>
      <div className="ca">
        <div className="ca-hdr">
          <div><div className="ca-title">Coordinators</div><div className="ca-sub">Manage coordinator accounts, access and exit status.</div></div>
          <button className="btn btn-purple" onClick={openAdd}><PlusIcon/>Add Coordinator</button>
        </div>

        <div className="panel">
            <div className="panel-hdr">
              <div className="search-wrap">
                <MagnifyingGlassIcon/>
                <input className="search-inp" placeholder="Search by name or employee ID…"
                  value={search} onChange={e=>setSearch(e.target.value)}/>
              </div>
              <label className="toggle-row">
                <input type="checkbox" checked={showInactive} onChange={e=>setShowInactive(e.target.checked)} style={{accentColor:'#8b5cf6'}}/>
                Show exited
              </label>
            </div>

            {loading ? (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px',color:'#9094a8',fontSize:'.82rem'}}>
                <span className="spin"/>Loading…
              </div>
            ) : coordFiltered.length === 0 ? (
              <div className="empty">{search ? 'No coordinators match your search.' : 'No coordinators yet. Click Add Coordinator to get started.'}</div>
            ) : (
              <div className="tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr>
                    <th style={{minWidth:160}}>Coordinator</th>
                    <th style={{minWidth:100,textAlign:'center'}}>Employee ID</th>
                    <th style={{minWidth:120,textAlign:'center'}}>Mobile</th>
                    <th style={{minWidth:130,textAlign:'center'}}>CNIC</th>
                    <th style={{minWidth:130,textAlign:'center'}}>Assigned Classes</th>
                    <th style={{minWidth:100,textAlign:'center'}}>Joining</th>
                    <th style={{minWidth:80,textAlign:'center'}}>Status</th>
                    <th style={{minWidth:100,textAlign:'center'}}>Exit Date</th>
                    <th style={{textAlign:'right',minWidth:120}}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {coordFiltered.map(c => {
                      const status  = getStatus(c);
                      const isActive = status === 'active';
                      const name    = c.user?.name||c.name||'—';
                      const email   = c.user?.email||c.email||'—';
                      const grades  = c.assignedGrades||[];
                      return (
                        <tr key={c._id}>
                          <td><div className="nc"><div className="av">{ini(name)}</div><div><div className="nm">{name}</div><div className="ne">{email}</div></div></div></td>
                          <td style={{textAlign:'center'}}><span className="mono">{c.employeeId||'—'}</span></td>
                          <td style={{textAlign:'center'}}><span className="mono">{c.mobile||'—'}</span></td>
                          <td style={{textAlign:'center'}}><span className="mono">{c.cnic||'—'}</span></td>
                          <td style={{textAlign:'center'}}>
                            {grades.length===0
                              ? <span style={{fontSize:'.72rem',color:'#10c98e',fontWeight:600}}>All classes</span>
                              : <div style={{display:'flex',flexWrap:'wrap',gap:3,justifyContent:'center'}}>
                                  {grades.slice(0,2).map((g,i)=>(
                                    <span key={i} className="grade-tag">
                                      {classes.find(cl=>String(cl._id)===String(g.classId?._id||g.classId||g))?.name||'?'}
                                    </span>
                                  ))}
                                  {grades.length>2&&<span className="grade-tag">+{grades.length-2}</span>}
                                </div>
                            }
                          </td>
                          <td style={{textAlign:'center'}}><span className="mono">{c.joiningDate?fmtD(c.joiningDate):'—'}</span></td>
                          <td style={{textAlign:'center'}}><span className={`pill ${status}`}>{status.charAt(0).toUpperCase()+status.slice(1)}</span></td>
                          <td style={{textAlign:'center'}}><span className="mono">{c.exitDate?fmtD(c.exitDate):'—'}</span></td>
                          <td>
                            <button className="act-btn edit" title="Edit" onClick={()=>openEdit(c)}><PencilIcon/></button>
                            <button className="act-btn history" title="Full History" onClick={()=>navigate(`/staff-history/${c.user?._id||c.user_id}?role=coordinator&isPortal=true`)}><ClockIcon/></button>
                            {isActive ? <>
                              <button className="act-btn resign"    title="Mark Resigned"   onClick={()=>openExit(c,'resigned')}><ArrowRightOnRectangleIcon/></button>
                              <button className="act-btn terminate" title="Mark Terminated" onClick={()=>openExit(c,'terminated')}><NoSymbolIcon/></button>
                              <button className="act-btn deactivate" title="Deactivate" onClick={()=>openStatusModal(c,'deactivate')}><LockClosedIcon/></button>
                            </> : c.exitType ? (
                              <button className="act-btn reactivate" title="Reactivate" onClick={()=>openStatusModal(c,'reactivate')}><CheckCircleIcon/></button>
                            ) : (
                              <button className="act-btn activate" title="Activate" onClick={()=>openStatusModal(c,'activate')}><LockOpenIcon/></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && coordFiltered.length > 0 && (
              <div className="cards">
                {coordFiltered.map(c => {
                  const status   = getStatus(c);
                  const isActive = status === 'active';
                  const name     = c.user?.name||c.name||'—';
                  const email    = c.user?.email||c.email||'—';
                  const grades   = c.assignedGrades||[];
                  return (
                    <div key={c._id} className="card">
                      <div className="card-top">
                        <div className="nc"><div className="av">{ini(name)}</div><div><div className="nm">{name}</div><div className="ne">{email}</div></div></div>
                        <span className={`pill ${status}`}>{status.charAt(0).toUpperCase()+status.slice(1)}</span>
                      </div>
                      <div className="card-row"><span className="card-label">Employee ID</span><span className="mono">{c.employeeId||'—'}</span></div>
                      <div className="card-row"><span className="card-label">Mobile</span><span className="mono">{c.mobile||'—'}</span></div>
                      <div className="card-row"><span className="card-label">CNIC</span><span className="mono">{c.cnic||'—'}</span></div>
                      <div className="card-row">
                        <span className="card-label">Classes</span>
                        {grades.length===0
                          ? <span style={{fontSize:'.72rem',color:'#10c98e',fontWeight:600}}>All classes</span>
                          : <div style={{display:'flex',flexWrap:'wrap',gap:3,justifyContent:'flex-end'}}>
                              {grades.map((g,i)=>(
                                <span key={i} className="grade-tag">
                                  {classes.find(cl=>String(cl._id)===String(g.classId?._id||g.classId||g))?.name||'?'}
                                </span>
                              ))}
                            </div>
                        }
                      </div>
                      <div className="card-row"><span className="card-label">Joining</span><span className="mono">{c.joiningDate?fmtD(c.joiningDate):'—'}</span></div>
                      <div className="card-row"><span className="card-label">Exit Date</span><span className="mono">{c.exitDate?fmtD(c.exitDate):'—'}</span></div>
                      <div className="card-actions">
                        <button className="act-btn edit" title="Edit" onClick={()=>openEdit(c)}><PencilIcon/></button>
                        <button className="act-btn history" title="Full History" onClick={()=>navigate(`/staff-history/${c.user?._id||c.user_id}?role=coordinator&isPortal=true`)}><ClockIcon/></button>
                        {isActive ? <>
                          <button className="act-btn resign"    title="Mark Resigned"   onClick={()=>openExit(c,'resigned')}><ArrowRightOnRectangleIcon/></button>
                          <button className="act-btn terminate" title="Mark Terminated" onClick={()=>openExit(c,'terminated')}><NoSymbolIcon/></button>
                          <button className="act-btn deactivate" title="Deactivate" onClick={()=>openStatusModal(c,'deactivate')}><LockClosedIcon/></button>
                        </> : c.exitType ? (
                          <button className="act-btn reactivate" title="Reactivate" onClick={()=>openStatusModal(c,'reactivate')}><CheckCircleIcon/></button>
                        ) : (
                          <button className="act-btn activate" title="Activate" onClick={()=>openStatusModal(c,'activate')}><LockOpenIcon/></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:520}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon purple"><BriefcaseIcon/></div>
                <div>
                  <div className="m-title">{editing?'Edit Coordinator':'Add Coordinator'}</div>
                  <div className="m-sub">{editing?'Update details.':'Create a new coordinator account.'}</div>
                </div>
              </div>
              <button className="m-close" onClick={()=>setModal(false)}><XMarkIcon/></button>
            </div>
            <form onSubmit={submit} style={{display:'contents'}} autoComplete="off">
              <div className="m-body">
                <div className="fg">
                  <FD label="Account"/>
                  <div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required/></div>
                  <div className="ff"><label className="fl">Email *</label><input type="email" className="fi" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required/></div>
                  <div className="ff full">
                    <label className="fl">Password {editing&&<span style={{fontSize:'.65rem',color:'#b0b5cc',fontWeight:400}}>leave blank to keep current</span>}</label>
                    <PasswordInput className="fi" autoComplete="new-password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} {...(!editing&&{required:true})}/>
                  </div>
                  <FD label="Details"/>
                  <div className="ff">
                    <label className="fl">
                      Employee ID
                      <span style={{fontSize:'.64rem',fontWeight:600,padding:'1px 7px',borderRadius:99,background:'#ecfdf5',color:'#10c98e',marginLeft:6}}>auto</span>
                      <span style={{fontSize:'.64rem',fontWeight:600,padding:'1px 7px',borderRadius:99,background:'#f0f2f7',color:'#9094a8',marginLeft:4}}>read-only</span>
                    </label>
                    <input className="fi mono" value={form.employeeId} readOnly style={{background:'#f0f2f7',color:'#9094a8',cursor:'not-allowed'}}/>
                  </div>
                  <div className="ff"><label className="fl">Mobile</label><input className="fi mono" placeholder="0300-1234567" value={form.mobile} onChange={e=>setForm(p=>({...p,mobile:formatPhone(e.target.value)}))}/></div>
                  <div className="ff"><label className="fl">CNIC</label><input className="fi mono" placeholder="35201-1234567-1" value={form.cnic} onChange={e=>setForm(p=>({...p,cnic:formatCnic(e.target.value)}))}/></div>
                  <div className="ff"><label className="fl">Joining Date</label><input type="date" className="fi" value={form.joiningDate} onChange={e=>setForm(p=>({...p,joiningDate:e.target.value}))}/></div>
                  <div className="ff"><label className="fl">Exit Date <span style={{fontWeight:400,textTransform:'none',fontSize:'.68rem',color:'#b0b5cc'}}>(if exited)</span></label><input type="date" className="fi" value={form.exitDate} onChange={e=>setForm(p=>({...p,exitDate:e.target.value}))}/></div>
                  <div className="ff full"><label className="fl">Address</label><input className="fi" placeholder="House/Street, City" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))}/></div>
                  <FD label="Assigned Classes"/>
                  <div className="ff full">
                    <span className="fh" style={{marginBottom:6}}>Leave all unselected for access to all classes.</span>
                    {!classes.length
                      ? <div style={{fontSize:'.76rem',color:'#9094a8',fontStyle:'italic',padding:'10px 14px',background:'#f8f9fc',borderRadius:9,border:'1px solid #e8ebf4'}}>No classes — add classes first.</div>
                      : <div className="grade-list">
                          {classes.map(cls => {
                            const on = form.assignedGrades.includes(String(cls._id));
                            return (
                              <div key={cls._id} className={`grade-item${on?' on':''}`} onClick={()=>toggleGrade(cls._id)}>
                                <div className="grade-chk"><svg viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                                <span style={{flex:1}}>{cls.name}</span>
                                {(cls.sections||[]).length>0&&<span style={{fontSize:'.68rem',color:'#9094a8'}}>{(cls.sections||[]).join(', ')}</span>}
                              </div>
                            );
                          })}
                        </div>
                    }
                    {form.assignedGrades.length===0&&classes.length>0&&(
                      <div style={{marginTop:6,fontSize:'.72rem',color:'#10c98e',fontWeight:600}}>✓ Full access to all classes</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-purple" disabled={saving}>{saving?'Saving…':editing?'Update':'Add Coordinator'}</button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* ── Exit Modal ── */}
      {exitModal && createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setExitModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className={`m-icon ${exitModal.exitType==='resigned'?'amber':'red'}`}>
                  {exitModal.exitType==='resigned'?<ArrowRightOnRectangleIcon/>:<NoSymbolIcon/>}
                </div>
                <div>
                  <div className="m-title">{exitModal.exitType==='resigned'?'Mark as Resigned':'Mark as Terminated'}</div>
                  <div className="m-sub">{exitModal.coord.user?.name||exitModal.coord.name}</div>
                </div>
              </div>
              <button className="m-close" onClick={()=>setExitModal(null)}><XMarkIcon/></button>
            </div>
            <div className="m-body">
              <div className={`warn-box ${exitModal.exitType==='terminated'?'warn-red':'warn-amber'}`}>
                ⚠️ Once marked as {exitModal.exitType}, this coordinator will lose portal access.
              </div>
              <div className="fg">
                <div className="ff"><label className="fl">Joining Date</label><input type="date" className="fi" value={exitForm.joiningDate} onChange={e=>setExitForm(p=>({...p,joiningDate:e.target.value}))}/></div>
                <div className="ff"><label className="fl">{exitModal.exitType==='resigned'?'Resignation Date':'Termination Date'}</label><input type="date" className="fi" value={exitForm.exitDate} onChange={e=>setExitForm(p=>({...p,exitDate:e.target.value}))}/></div>
                <div className="ff full"><label className="fl">Reason</label><textarea className="fta" required value={exitForm.exitNote} onChange={e=>setExitForm(p=>({...p,exitNote:e.target.value}))} placeholder="Reason…"/></div>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={()=>setExitModal(null)}>Cancel</button>
              <button className={`btn ${exitModal.exitType==='resigned'?'btn-amber':'btn-red'}`} onClick={submitExit} disabled={saving}>
                {saving?'Saving…':`Confirm ${exitModal.exitType==='resigned'?'Resignation':'Termination'}`}
              </button>
            </div>
          </div>
        </div>

      , document.body)}

      <ConfirmModal
        open={!!statusModal}
        title={statusModal?.action === 'deactivate' ? 'Deactivate Coordinator' : statusModal?.action === 'activate' ? 'Activate Coordinator' : 'Reactivate Coordinator'}
        subtitle={statusModal?.coord?.user?.name || statusModal?.coord?.name}
        message={
          statusModal?.action === 'deactivate'
            ? 'This coordinator will immediately lose portal access. You can activate them again at any time.'
            : statusModal?.action === 'activate'
            ? 'This coordinator will regain portal access immediately.'
            : `Reactivate ${statusModal?.coord?.user?.name || statusModal?.coord?.name}'s account? This restores their portal access.`
        }
        confirmLabel={statusModal?.action === 'deactivate' ? 'Deactivate' : statusModal?.action === 'activate' ? 'Activate' : 'Reactivate'}
        danger={statusModal?.action === 'deactivate'}
        onConfirm={confirmStatusAction}
        onCancel={()=>setStatusModal(null)}
        loading={statusSaving}
      />
    </>
  );
}